import API_BASE_FORMATOS_URL from "../../config/config_formatos";

const COMPETITION_REPORT_PATH = "/api/sismaster/competition-report";
const getBase = () => String(API_BASE_FORMATOS_URL).replace(/\/$/, "");

const EVENT_ID = 223;

// ✅ Mapeo de idsport Hayllis → sportId de la API externa
const HAYLLIS_TO_EXTERNAL_SPORT_ID = {
  8:  4,  // Judo
  9:  5,  // Wushu
  25: 6,  // Lucha Olímpica
};

/**
 * Resuelve el sportId externo dado el idsport del Hayllis.
 * Si no hay mapeo (ej: Judo sin pasar idsport), usa 4 como fallback legacy.
 */
export const resolveExternalSportId = (idsport) =>
  HAYLLIS_TO_EXTERNAL_SPORT_ID[Number(idsport)] ?? 4;

// ─── Builders de URL parametrizados ─────────────────────────────────────────
export const buildCategoriasEndpoint = (idsport) =>
  `${getBase()}${COMPETITION_REPORT_PATH}/${EVENT_ID}?sportId=${resolveExternalSportId(idsport)}`;

export const buildFasesEndpoint = (eventCategoryId) =>
  `${getBase()}${COMPETITION_REPORT_PATH}/${EVENT_ID}?eventCategoryId=${eventCategoryId}`;

export const buildResultadosFaseEndpoint = (eventCategoryId, phaseId) =>
  `${getBase()}${COMPETITION_REPORT_PATH}/${EVENT_ID}?eventCategoryId=${eventCategoryId}&phaseId=${phaseId}`;

// ─── Extractores ─────────────────────────────────────────────────────────────
export const extractCategoriasFromReport = (report) => {
  if (!report?.sports?.length) return [];
  return report.sports[0]?.categories || [];
};

export const extractFasesFromReport = (report) => {
  if (!report?.sports?.length) return [];
  return report.sports[0]?.categories?.[0]?.phases || [];
};

export const extractFaseFromReport = (report) =>
  report?.sports?.[0]?.categories?.[0]?.phases?.[0] || null;

// ─── Formatters ──────────────────────────────────────────────────────────────
export const normalizeText = (value) => {
  if (!value) return "No especificado";
  return String(value).trim();
};

export const formatCategoriaSubtitle = (categoria) => {
  const gender   = normalizeText(categoria?.gender);
  const ageGroup = categoria?.ageGroup ? normalizeText(categoria.ageGroup) : null;
  return [gender, ageGroup].filter(Boolean).join(" · ");
};

export const getParticipantsLabel = (totalParticipants = 0) => {
  const total = Number(totalParticipants) || 0;
  return `${total} participante${total === 1 ? "" : "s"}`;
};

// ─── Mapeo de phaseType ───────────────────────────────────────────────────────
export const getPhaseTypeProps = (phaseType) => {
  const normalized = String(phaseType || "").toLowerCase();
  const MAP = {
    eliminacion: { label: "Eliminación directa", color: "error"   },
    grupo:       { label: "Fase de grupos",       color: "primary" },
    mejor_de_3:  { label: "Mejor de 3",           color: "info"    },
    round_robin: { label: "Round Robin",           color: "success" },
    suizo:       { label: "Sistema suizo",         color: "warning" },
    final:       { label: "Final",                 color: "error"   },
    semifinal:   { label: "Semifinal",             color: "warning" },
    repechaje:   { label: "Repechaje",             color: "default" },
  };
  return MAP[normalized] ?? { label: normalizeText(phaseType), color: "default" };
};

// ─── Status chips ─────────────────────────────────────────────────────────────
export const getStatusChipProps = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "pendiente")  return { label: "Pendiente",  color: "warning" };
  if (normalized === "en curso")   return { label: "En curso",   color: "info"    };
  if (normalized === "finalizado") return { label: "Finalizado", color: "success" };
  return { label: normalizeText(status), color: "default" };
};

export const getBracketStatusProps = (status) => {
  const n = String(status || "").toLowerCase();
  if (n === "finalizado") return { label: "Finalizado", color: "success" };
  if (n === "en curso")   return { label: "En curso",   color: "info"    };
  if (n === "pendiente")  return { label: "Pendiente",  color: "warning" };
  return { label: status || "—", color: "default" };
};

// ─── ✅ victoryType — nuevo helper para Lucha/Judo/Wushu ─────────────────────
const VICTORY_TYPE_MAP = {
  VPO:  { label: "VPO",  title: "Victoria por puntos",        color: "bg-blue-50 text-blue-700 border-blue-200"   },
  VPO1: { label: "VPO1", title: "Victoria por puntos (1)",    color: "bg-blue-50 text-blue-700 border-blue-200"   },
  VFA:  { label: "VFA",  title: "Victoria por abandono",      color: "bg-orange-50 text-orange-700 border-orange-200" },
  VSU:  { label: "VSU",  title: "Victoria por superioridad",  color: "bg-green-50 text-green-700 border-green-200" },
  VSU1: { label: "VSU1", title: "Victoria por superioridad (1)", color: "bg-green-50 text-green-700 border-green-200" },
  VCA:  { label: "VCA",  title: "Victoria por caída",         color: "bg-amber-50 text-amber-700 border-amber-200" },
  WO:   { label: "W/O",  title: "Walkover",                   color: "bg-gray-100 text-gray-500 border-gray-200"  },
};

export const getVictoryTypeProps = (victoryType) =>
  VICTORY_TYPE_MAP[String(victoryType || "").toUpperCase()] ?? {
    label: victoryType,
    title: victoryType,
    color: "bg-gray-100 text-gray-500 border-gray-200",
  };

// ─── Ordenamiento ─────────────────────────────────────────────────────────────
const parseWeight = (name = "") => {
  const cleaned = String(name).replace(",", ".").trim();
  const match   = cleaned.match(/([+-]?\d+(\.\d+)?)/);
  if (!match) return Number.POSITIVE_INFINITY;
  return Number(match[1]);
};

export const sortCategorias = (categorias = []) =>
  [...categorias].sort((a, b) => {
    const wA = parseWeight(a?.categoryName);
    const wB = parseWeight(b?.categoryName);
    if (wA !== wB) return wA - wB;
    return String(a?.categoryName || "").localeCompare(
      String(b?.categoryName || ""), "es", { numeric: true, sensitivity: "base" }
    );
  });

export const sortFases = (fases = []) =>
  [...fases].sort((a, b) => {
    const orderA = a?.displayOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b?.displayOrder ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return String(a?.phaseName || "").localeCompare(String(b?.phaseName || ""), "es", { sensitivity: "base" });
  });

// ─── Summary ──────────────────────────────────────────────────────────────────
export const getSummary = (categorias = []) => ({
  totalCategorias: categorias.length,
  categoriasConParticipantes: categorias.filter(
    (item) => Number(item?.totalParticipants || 0) > 0
  ).length,
  totalParticipantes: categorias.reduce(
    (acc, item) => acc + Number(item?.totalParticipants || 0), 0
  ),
});

// ─── Helpers de brackets ──────────────────────────────────────────────────────
export const getAthleteName = (athleteEntry) =>
  athleteEntry?.athlete?.fullName || athleteEntry?.fullName || "Atleta desconocido";

export const getAthleteInstitution = (athleteEntry) =>
  athleteEntry?.athlete?.institution?.name || athleteEntry?.institution?.name || "";

export const getAthleteInstitutionLogo = (athleteEntry) =>
  athleteEntry?.athlete?.institution?.logoUrl || athleteEntry?.institution?.logoUrl || null;

export const isWinner = (bracket, registrationId) =>
  bracket?.winner?.registrationId === registrationId;

export const getRoundLabel = (round) => {
  const MAP = {
    cuartos:              "Cuartos de final",
    semifinal:            "Semifinal",
    final:                "Final",
    "3er":                "Tercer lugar",
    repechaje:            "Repechaje",
    repechaje_octavos:   "Repechaje — Octavos",    
    repechaje_cuartos:   "Repechaje — Cuartos",
    repechaje_semifinal:  "Repechaje — Semifinal",  
    repechaje_final:      "Repechaje — Final",       
    serie:                "Serie",
    "1": "Ronda 1", "2": "Ronda 2", "3": "Ronda 3",
  };
  return MAP[String(round || "").toLowerCase()] ?? `Ronda ${round}`;
};

export const groupBracketsByRound = (brackets = []) => {
  const ROUND_ORDER = [
    "octavos",
    "cuartos",
    "semifinal",
    "final",
    "repechaje_octavos",   
    "repechaje_cuartos",    
    "repechaje_semifinal",
    "repechaje_final",
    "repechaje",
    "3er",
    "serie",
    "1", "2", "3",
  ];


  const grouped = brackets.reduce((acc, bracket) => {
    const key = String(bracket.round || "otro");
    if (!acc[key]) acc[key] = [];
    acc[key].push(bracket);
    return acc;
  }, {});

  return Object.entries(grouped).sort(([roundA], [roundB]) => {
    const idxA = ROUND_ORDER.indexOf(roundA.toLowerCase());
    const idxB = ROUND_ORDER.indexOf(roundB.toLowerCase());
    return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
  });
};

// ─── Helpers de podio ─────────────────────────────────────────────────────────
export const getRankMedal = (rank) => {
  if (rank === 1) return { emoji: "🥇", label: "1°", color: "gold"    };
  if (rank === 2) return { emoji: "🥈", label: "2°", color: "silver"  };
  if (rank === 3) return { emoji: "🥉", label: "3°", color: "#cd7f32" };
  return           { emoji: "",   label: `${rank}°`, color: "text.secondary" };
};